
/* eslint-disable complexity */
import { useMemo } from 'react';
import { OpenSuccessNotification, OpenErrorNotification } from 'protocol-setup/components/notification';
import { postWithAuth } from 'protocol-setup/redux/makeFetch';
import { defaultErrorMessage, pendingSignOffUrl, signoffReminderUrl } from 'protocol-setup/redux/constants';
import { parse, format } from 'date-fns';
import { useSetupPermission } from 'protocol-setup/helpers';
import ProtectionElements from 'protocol-setup/helpers/ProtectionElement';
import {
  groupAdminSchema,
  openAdminSchema,
  irocAdminSchema
} from './validationSchemas';




export const convertToFullTimeFormat = (dateStr) => {
  // Parse the input date string with the format "dd-MMM-yyyy hh:mm a"
  const parsedDate = parse(dateStr, 'dd-MMM-yyyy hh:mm a', new Date());
  
  // Format it back with seconds and append " EST" as literal string
  return `${format(parsedDate, 'dd-MMM-yyyy hh:mm:ss a')} EST`;
};


// Identify which section has unsaved changes
export const getDirtySection = (dirtyFields) => {
  const groupAdminFields = ['releaseDateTime', 'groupAdminSignOff', 'groupAdminComments'];
  const openAdminFields = ['openAdminSignOff', 'openAdminComments'];
  const irocAdminFields = ['irocAdminSignOff', 'irocAdminComments'];

  const hasGroupAdminChanges = groupAdminFields.some(field => dirtyFields[field]);
  const hasOpenAdminChanges = openAdminFields.some(field => dirtyFields[field]);
  const hasIrocAdminChanges = irocAdminFields.some(field => dirtyFields[field]);

  const sectionsChanged = [
    hasGroupAdminChanges,
    hasOpenAdminChanges,
    hasIrocAdminChanges
  ].filter(Boolean).length;

  if (sectionsChanged > 1) return 'multiple';
  if (hasGroupAdminChanges) return 'groupAdmin';
  if (hasOpenAdminChanges) return 'openAdmin';
  if (hasIrocAdminChanges) return 'irocAdmin';

  return null;
};


const getSucessMessage = (section) => {
  switch (section) {
    case 'groupAdmin':
      return "Group Admin signoff form successfully submitted.";
    case 'openAdmin':
      return "Open Admin signoff form successfully submitted.";
    case 'irocAdmin':
      return "IROC Admin signoff form successfully submitted.";
    default:
      return "Signoff form successfully submitted.";
  }
}



export const validateSection = async (schema, fields, getValues) => {
  try {
    const values = getValues();
    const sectionData = {};
    fields.forEach(field => {
      sectionData[field] = values[field];
    });
    await schema.validate(sectionData, { abortEarly: false });
    return { isValid: true, fieldErrors: [] };
  } catch (error) {
    if (error.inner) {
      const fieldErrors = error.inner.map(err => ({
        field: err.path,
        message: err.message
      }));
      return { isValid: false, fieldErrors };
    }
    return { isValid: false, fieldErrors: [] };
  }
};


// Submit handler for individual sections
export const handleSectionSubmit = async ({
  section,
  getValues,
  protocolSetupData,
  fetchPsData,
  dispatchRefreshEvent,
  setError
}) => {
  try {
    const values = getValues();
    let validation;
    let payload = {
      requestNumber: protocolSetupData?.requestNumber || null,
      completed: values.completed || false
    };
    if (protocolSetupData?.signoffPending?.psrsoId) {
      payload.psrsoId = protocolSetupData.signoffPending.psrsoId;
    }

    switch (section) {
      case 'groupAdmin':
        validation = await validateSection(groupAdminSchema, [
          'releaseDateTime',
          'groupAdminSignOff',
          'groupAdminComments'
        ], getValues);

        if (!validation.isValid) {

          (validation.fieldErrors || []).forEach(({ field, message }) => {
            setError(field, {
              type: 'manual',
              message
            });
          });
          return false;
        }

        payload = {
          ...payload,

          releaseDt: convertToFullTimeFormat(values.releaseDateTime),
          groupAdminSignoff: values.groupAdminSignOff,
          groupAdminComments: values.groupAdminComments,
          notificationId: "PndSOGrpSav"
        };
        break;

      case 'openAdmin':
        validation = await validateSection(openAdminSchema, [
          'openAdminSignOff',
          'openAdminComments'
        ], getValues);

        if (!validation.isValid) {
          (validation.fieldErrors || []).forEach(({ field, message }) => {
            setError(field, {
              type: 'manual',
              message
            });
          });
          return false;
        }

        payload = {
          ...payload,
          openAdminSignoff: values.openAdminSignOff,
          openAdminComments: values.openAdminComments,
          notificationId: "PndSOOpn"
        };
        break;

      case 'irocAdmin':
        validation = await validateSection(irocAdminSchema, [
          'irocAdminSignOff',
          'irocAdminComments'
        ], getValues);

        if (!validation.isValid) {
          (validation.fieldErrors || []).forEach(({ field, message }) => {
            setError(field, {
              type: 'manual',
              message
            });
          });
          return false;
        }

        payload = {
          ...payload,
          irocAdminSignoff: values.irocAdminSignOff,
          irocAdminComments: values.irocAdminComments,
          notificationId: "PndSOIROC"
        };
        break;

      default:
        break;
    }
    // Override notificationId if completed is true
    if (values.completed) {
      payload.notificationId = "PndSOComp";
    }
    // Call API
    await postWithAuth(pendingSignOffUrl, payload);

    if (fetchPsData) {
      await fetchPsData();
      dispatchRefreshEvent()
    }


    OpenSuccessNotification({
      message: getSucessMessage(section),
      autoHide: true
    });
    return true;
  } catch (e) {
    OpenErrorNotification({
      message: defaultErrorMessage,
      autoHide: true
    });
    return false;
  }
};


// Main submit handler that detects which section to submit
export const onSubmit = async ({
  dirtyFields,
  getValues,
  protocolSetupData,
  fetchPsData,
  dispatchRefreshEvent,
  setError
}) => {
  const dirtySection = getDirtySection(dirtyFields);

  if (!dirtySection) {
    OpenErrorNotification({
      message: 'No changes detected. Please modify at least one section before submitting.',
      autoHide: true
    });
    return false;
  }

  if (dirtySection === 'multiple') {
    OpenErrorNotification({
      message: 'Please save one section at a time. Multiple sections cannot be saved together.',
      autoHide: true
    });
    return false;
  }

  return handleSectionSubmit({
    section: dirtySection,
    getValues,
    protocolSetupData,
    fetchPsData,
    dispatchRefreshEvent,
    setError
  });
};

export function countSectionsSubmitted(signoffPending) {
  let count = 0;
  if (signoffPending.openAdminSubmittedBy && signoffPending.openAdminSubmittedBy.trim() !== "") count++;
  if (signoffPending.groupAdminSubmittedBy && signoffPending.groupAdminSubmittedBy.trim() !== "") count++;
  if (signoffPending.irocAdminSubmittedBy && signoffPending.irocAdminSubmittedBy.trim() !== "") count++;

  return count;
}



export const getSaveButtonLabel = (protocolSetupData, releaseDateTimeUpdated, readOnly) => {
  const isIrocCredentialingInfoOpen = protocolSetupData?.isIrocCredentialingInfoOpen || false;
  const signoffPending = protocolSetupData?.signoffPending || {};
  const counts = countSectionsSubmitted(signoffPending);
  if (releaseDateTimeUpdated || readOnly) {
    return "Save & Send Update";
  }
  return (isIrocCredentialingInfoOpen && counts === 2) || (!isIrocCredentialingInfoOpen && counts === 1)
    ? "Submit"
    : "Save";
};


export const getActiveTab = (fieldName) => {
  const groupAdminFields = ['releaseDateTime', 'groupAdminSignOff', 'groupAdminComments'];
  const openAdminFields = ['openAdminSignOff', 'openAdminComments'];
  const irocAdminFields = ['irocAdminSignOff', 'irocAdminComments'];

  if (groupAdminFields.includes(fieldName)) {
    return 'groupAdmin';
  }
  if (openAdminFields.includes(fieldName)) {
    return 'openAdmin';
  }
  if (irocAdminFields.includes(fieldName)) {
    return 'irocAdmin';
  }
  return null;
};

// for users with certain roles, view the Pending signoff section in Edit Mode
// until any of the Optional Tracking Data fields are saved in the Completed signoff
// eventhough the pending signoff is marked as completed.
export const checkFormReadOnlyState = (hasValidRoleAndNotComplete, isFormReadOnly) => {
  if (hasValidRoleAndNotComplete) {
    return false;
  }
  return isFormReadOnly;
}


export const useFormEditability = (readOnly, signoffData, requestStatus) => {
  // Check user permissions
  const canEditGroupAdminSection = useSetupPermission(
    ProtectionElements.OPEN_SIGNOFF_PENDINGSIGNOFF_GROUP
  );
  const canEditOpenAdminSection = useSetupPermission(
    ProtectionElements.OPEN_SIGNOFF_PENDINGSIGNOFF_CTSU
  );
  const canEditIrocAdminSection = useSetupPermission(
    ProtectionElements.OPEN_SIGNOFF_PENDINGSIGNOFF_IROC
  );


  // Calculate editability based on all conditions
  const editability = useMemo(() => {
    // Check if sections have been submitted by someone
    const groupAdminSubmittedBy = signoffData?.groupAdminSubmittedBy || "";
    const openAdminSubmittedBy = signoffData?.openAdminSubmittedBy || "";
    const irocAdminSubmittedBy = signoffData?.irocAdminSubmittedBy || "";

    // Check if Optional Tracking Data fields are still null (not completed)
    const setupCompletedProdNotComplete = signoffData?.setupCompletedProd === null;
    const formsActivatedProdNotComplete = signoffData?.formsActivatedProd === null;

    const isCanceledOrRetired = requestStatus === 'CANCELED' || requestStatus === 'RETIRED' || requestStatus === 'COMPLETED';

    // Special case: Users with valid roles can edit Release Date
    // until any of the Optional Tracking Data fields are saved
    // BUT NOT if status is CANCELED or RETIRED
    const canEditReleaseDateDueToValidRole =
      !isCanceledOrRetired &&
      setupCompletedProdNotComplete &&
      formsActivatedProdNotComplete;

    // Determine section editability
    // Can edit IF: (has permission) AND (nobody has submitted) AND (not read-only) AND (not CANCELED/RETIRED)
    const isGroupAdminSectionEditable =
      !isCanceledOrRetired && canEditGroupAdminSection && !groupAdminSubmittedBy && !readOnly;
    const isOpenAdminSectionEditable =
      !isCanceledOrRetired && canEditOpenAdminSection && !openAdminSubmittedBy && !readOnly;
    const isIrocAdminSectionEditable =
      !isCanceledOrRetired && canEditIrocAdminSection && !irocAdminSubmittedBy && !readOnly;

    // Release Date is editable if: user has valid role exception OR normal group admin edit permission
    const isReleaseDateTimeEditable =
      canEditReleaseDateDueToValidRole || isGroupAdminSectionEditable;

    // Form should show in read-only mode if:
    // - readOnly prop is true AND user doesn't have valid role exception
    // - OR status is CANCELED or RETIRED
    const shouldShowReadOnly = (readOnly && !canEditReleaseDateDueToValidRole) || isCanceledOrRetired;

    // Determine individual field editability
    return {
      // Section-level editability
      isGroupAdminSectionEditable,
      isOpenAdminSectionEditable,
      isIrocAdminSectionEditable,

      // Individual field editability
      fields: {
        releaseDateTime: isReleaseDateTimeEditable,
        groupAdminSignOff: isGroupAdminSectionEditable,
        groupAdminComments: isGroupAdminSectionEditable,
        openAdminSignOff: isOpenAdminSectionEditable,
        openAdminComments: isOpenAdminSectionEditable,
        irocAdminSignOff: isIrocAdminSectionEditable,
        irocAdminComments: isIrocAdminSectionEditable
      },

      // Overall form state
      shouldShowReadOnly,
      canEditReleaseDateDueToValidRole
    };
  }, [
    readOnly,
    signoffData,
    canEditGroupAdminSection,
    canEditOpenAdminSection,
    canEditIrocAdminSection,
    requestStatus
  ]);

  return editability;
};



export function getLatestSignoffInfo(signoffData) {
  const signoffs = [
    {
      type: 'openAdmin',
      date: signoffData.openAdminSubmittedDate,
      submittedBy: signoffData.openAdminSubmittedBy
    },
    {
      type: 'groupAdmin',
      date: signoffData.groupAdminSubmittedDate,
      submittedBy: signoffData.groupAdminSubmittedBy
    },
    {
      type: 'irocAdmin',
      date: signoffData.irocAdminSubmittedDate,
      submittedBy: signoffData.irocAdminSubmittedBy
    }
  ];

  // Filter out entries with null/undefined dates
  const validSignoffs = signoffs.filter(s => s.date);

  if (validSignoffs.length === 0) {
    return null;
  }

  // Parse date string format: "07-Oct-2025 01:31 PM"
  const parseDate = (dateStr) => {
    return new Date(dateStr);
  };

  // Find the latest signoff
  const latestSignoff = validSignoffs.reduce((latest, current) => {
    const currentDate = parseDate(current.date);
    const latestDate = parseDate(latest.date);
    return currentDate > latestDate ? current : latest;
  }, validSignoffs[0]); // Using first element as initial value

  return {
    submittedDate: latestSignoff.date,
    submitterName: latestSignoff.submittedBy
  };
}

export const onSendReminder = async (requestNumber, isOpenAdmin) => {

  try {
    await postWithAuth(signoffReminderUrl, {
      "notificationId": isOpenAdmin ? 'PndSOOpn' : 'PndSOGrpSavSnd',
      "requestNumber": requestNumber
    });
    OpenSuccessNotification({
      message: "Reminder sent successfully.",
      autoHide: true
    });
  } catch (error) {
    OpenErrorNotification({
      message: defaultErrorMessage,
      autoHide: true
    });
  }
}
